import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionContext } from 'session'
import { formatErrorMessages } from 'interfaces'
import { CenteredCircularProgress, useGlobalMessageHandler, useProtectedRoleHoldersLut, useUsRoleTypeLuts } from 'components'
import {
  type ExecOrgParentSelection,
  ExecutiveOrgParentPicker,
  effectiveParentOrg
} from 'components/us/executives/executive-org-parent-picker'
import {
  type ExecutiveOrganization,
  getExecutiveOrgDescendants,
  getExecutiveOrganization,
  getProtectedRoleHolders,
  moveExecutiveOrganization
} from 'services'
import { FEDERAL_EXECUTIVE_ROLES, STATE_EXECUTIVE_ROLES, isStateExecutiveRole } from 'utils'

const EMPTY_SELECTION: ExecOrgParentSelection = {
  roleId: null,
  state: null,
  parentOffice: null,
  childOffice: null
}

const FEDERAL_BRANCH_ROLE_IDS = new Set<number>(FEDERAL_EXECUTIVE_ROLES)
const STATE_BRANCH_ROLE_IDS = new Set<number>(STATE_EXECUTIVE_ROLES)

export const ExecutiveOrgMovePage: React.FC = () => {
  const { execOrgId } = useParams<{ execOrgId: string }>()
  const parsedExecOrgId = execOrgId != null ? parseInt(execOrgId, 10) : 0
  const navigate = useNavigate()
  const { changeModuleName } = useSessionContext()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { lutValues: roleLut } = useUsRoleTypeLuts()
  const { setLutValues: setProtectedRoleHoldersLut } = useProtectedRoleHoldersLut()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [org, setOrg] = useState<ExecutiveOrganization | null>(null)
  const [selection, setSelection] = useState<ExecOrgParentSelection>(EMPTY_SELECTION)
  const [newRoleId, setNewRoleId] = useState<number | null>(null)
  const [excludeIds, setExcludeIds] = useState<number[]>([])

  useEffect(() => { changeModuleName('Move Executive Organization') }, [changeModuleName])

  // The org and its whole subtree are invalid destinations (the server rejects
  // a move under self/descendant). Pre-compute the subtree ids so the picker
  // hides them instead of surfacing the error only on submit.
  useEffect(() => {
    if (org == null) return
    getExecutiveOrgDescendants(org.id)
      .then(all => { setExcludeIds([org.id, ...all.map(node => node.id)]) })
      .catch(() => { setExcludeIds([org.id]) })
  }, [org])

  useEffect(() => {
    if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
      setLoading(false)
      return
    }
    setLoading(true)
    getExecutiveOrganization(parsedExecOrgId)
      .then(loaded => {
        setOrg(loaded)
        setNewRoleId(loaded.role.id)
        const stateRole = isStateExecutiveRole(loaded.role.id)
        setSelection({
          roleId: loaded.role.id,
          state: stateRole ? { id: loaded.state.id } : { id: 0 },
          parentOffice: null,
          childOffice: null
        })
      })
      .catch(() => { showErrorMessage('Failed to load executive organization.') })
      .finally(() => { setLoading(false) })
  }, [parsedExecOrgId, showErrorMessage])

  const destination = effectiveParentOrg(selection)
  const willRetype = org != null && newRoleId != null && newRoleId !== org.role.id
  const canSave =
    org != null &&
    selection.roleId != null &&
    newRoleId != null &&
    destination != null &&
    destination.id !== org.id &&
    !excludeIds.includes(destination.id) &&
    (!isStateExecutiveRole(selection.roleId) || selection.state != null)

  const handleSave = (): void => {
    if (!canSave || newRoleId == null || destination == null) return
    setSaving(true)
    moveExecutiveOrganization(parsedExecOrgId, {
      role: { id: newRoleId },
      parentOrg: { id: destination.id }
    })
      .then(response => {
        if (response.succeeded && response.result != null) {
          showSuccessMessage('Executive organization moved.')
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
          navigate(`/us/executives/orgs/${parsedExecOrgId}`)
        } else {
          const message = formatErrorMessages(response.errors)
          showErrorMessage(
            message !== '' ? message : (response.message ?? 'Failed to move organization.')
          )
        }
      })
      .catch(() => { showErrorMessage('Failed to move organization.') })
      .finally(() => { setSaving(false) })
  }

  if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
    return <Box>Invalid Executive Organization ID</Box>
  }
  if (loading) {
    return <CenteredCircularProgress loading={loading} size={100} position='absolute' />
  }
  if (org == null) {
    return <Box>Executive Organization not found.</Box>
  }

  const branchRoleIds = isStateExecutiveRole(org.role.id)
    ? STATE_BRANCH_ROLE_IDS
    : FEDERAL_BRANCH_ROLE_IDS
  const newRoleOptions = Array.from(branchRoleIds)
    .flatMap(id => {
      const role = roleLut.roles.get(id)
      return role == null ? [] : [role]
    })
    .sort((a, b) => a.desc.localeCompare(b.desc))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box width='97%' sx={{ pb: 1 }}>
        <Typography variant='h4' sx={{ mb: 1 }}>Move Executive Organization</Typography>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant='subtitle1'>{org.name}</Typography>
          {org.parentOrg != null && (
            <Typography variant='body2' color='text.secondary'>Current parent: {org.parentOrg.name}</Typography>
          )}
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Choose the destination parent (Parent Role and State only filter the parent list), then
          the role this organization should have after the move. The organization moves with its
          sub-organizations and people.
        </Typography>
        {willRetype && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Changing the role re-types this organization and all of its sub-organizations.
            Every position in this organization and its sub-organizations, including vacancies, must already have
            {' '}{roleLut.roles.get(newRoleId ?? -1)?.desc ?? 'the new role'} as its Branch Role &mdash; change each
            position on its own page first, or keep the current role.
          </Alert>
        )}
        <Grid container spacing={2}>
          <ExecutiveOrgParentPicker
            value={selection}
            onChange={setSelection}
            disabled={saving}
            excludeIds={excludeIds}
            roleLabel='Parent Role *'
            allowedRoleIds={branchRoleIds}
          />
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Role After Move *</InputLabel>
              <Select
                label='Role After Move *'
                disabled={saving}
                value={newRoleId ?? ''}
                onChange={(event) => { setNewRoleId(Number(event.target.value)) }}
              >
                {newRoleOptions.length === 0
                  ? <MenuItem value={org.role.id}>Current role</MenuItem>
                  : newRoleOptions.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.id === org.role.id ? `${role.desc} (current)` : role.desc}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                variant='outlined'
                disabled={saving}
                onClick={() => { navigate(`/us/executives/orgs/${parsedExecOrgId}`) }}
              >
                Cancel
              </Button>
              <Button variant='contained' disabled={!canSave || saving} onClick={handleSave}>
                Move
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
